'use client';

import { c as _c } from "react/compiler-runtime";
import { zodResolver } from '@hookform/resolvers/zod';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { amplitude } from '@/libs/amplitude';
import { Button } from '@/shared/components/Button';
import { prepareBodyDataEmail, prepareBodyDataPhone } from '../helpers/prepareBodyData';
import { useAuth } from '../hooks/useAuth';
import { AuthPasswordInput } from '../ui/AuthPasswordInput';
import { EmailPhoneInput } from '../ui/EmailPhoneInput';
import { loginEmailSchema, loginPhoneSchema } from './shema';
type LoginMode = 'email' | 'phone';
export type LoginFormEmailValues = {
  email: string;
  password: string;
};
export type LoginFormPhoneValues = {
  phone: string;
  password: string;
};
export const LoginForm = () => {
  const $ = _c(122);
  const t = useTranslations("Auth");
  const {
    login,
    isLoading
  } = useAuth();
  const [loginMode] = useState("email");
  const locale = useLocale();
  const router = useRouter();
  const searchParams = useSearchParams();
  const callbackUrl = searchParams.get("callbackUrl") || `/${locale}`;
  let t0;
  if ($[0] !== t) {
    t0 = zodResolver(loginEmailSchema(t));
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      email: "",
      password: ""
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t0) {
    t2 = {
      mode: "onChange",
      resolver: t0,
      defaultValues: t1
    };
    $[3] = t0;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const emailForm = useForm(t2);
  let t3;
  if ($[5] !== t) {
    t3 = zodResolver(loginPhoneSchema(t));
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      phone: "",
      password: ""
    };
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t3) {
    t5 = {
      mode: "onChange",
      resolver: t3,
      defaultValues: t4
    };
    $[8] = t3;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const phoneForm = useForm(t5);
  const onSubmitEmail = async data => {
    const bodyData = prepareBodyDataEmail({
      email: data.email,
      password: data.password
    });
    emailForm.clearErrors();
    const result = await login(bodyData);
    if (result.success) {
      amplitude.setUserId(result.data?.user?.id);
      const identifyObj = new amplitude.Identify().set("email", data.email).set("loginDate", new Date().toISOString());
      amplitude.identify(identifyObj);
      amplitude.track("Login", {
        method: "Email"
      });
      const redirectUrl = callbackUrl.startsWith("/") ? callbackUrl : `/${locale}`;
      router.push(redirectUrl);
    } else {
      if (result.error === "User is banned") {
        emailForm.setError("email", {
          message: t("Errors.UserDelete")
        });
      } else {
        emailForm.setError("password", {
          message: result.error
        });
      }
    }
  };
  const onSubmitPhone = async data_0 => {
    const bodyData_0 = prepareBodyDataPhone({
      phone: data_0.phone,
      password: data_0.password
    });
    phoneForm.clearErrors();
    const result_0 = await login(bodyData_0);
    if (result_0.success) {
      amplitude.setUserId(result_0.data?.user?.id);
      const identifyObj_0 = new amplitude.Identify().set("phone", data_0.phone).set("loginDate", new Date().toISOString());
      amplitude.identify(identifyObj_0);
      amplitude.track("Login", {
        method: "Phone"
      });
      const redirectUrl_0 = callbackUrl.startsWith("/") ? callbackUrl : `/${locale}`;
      router.push(redirectUrl_0);
    } else {
      if (result_0.error === "User is banned") {
        phoneForm.setError("phone", {
          message: t("Errors.UserDelete")
        });
      } else {
        phoneForm.setError("password", {
          message: result_0.error
        });
      }
    }
  };
  let t6;
  if ($[10] !== t) {
    t6 = t("LogInToYourAccount");
    $[10] = t;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t6) {
    t7 = <h1 className={"text-fill-transparent bold tablet:leading-1 w-full bg-gradient-to-r from-[#DA3D15] to-[#FE7801] bg-clip-text  text-center font-secondary text-[30px] font-extrabold uppercase italic\n        leading-none tablet:text-nowrap tablet:text-[34px] "}>{t6}</h1>;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const t8 = `/${locale}/auth/signup`;
  let t9;
  if ($[14] !== t) {
    t9 = t("OrSignUp");
    $[14] = t;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t9) {
    t10 = <span className="cursor-pointer text-[30px] font-extrabold uppercase italic  text-white tablet:text-[34px] ">{t9}</span>;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t8) {
    t11 = <Link href={t8} onClick={_temp}>{t10}</Link>;
    $[18] = t10;
    $[19] = t8;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div className="h-[4px] w-full bg-white  [clip-path:polygon(0_100%,3%_0,100%_0,97%_100%)]" />;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t11) {
    t13 = <div className="flex flex-col items-center justify-center gap-1">{t11}{t12}</div>;
    $[22] = t11;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t13 || $[25] !== t7) {
    t14 = <div className=" flex flex-col items-center justify-center"><div className="flex w-full flex-col items-center justify-center">{t7}{t13}</div></div>;
    $[24] = t13;
    $[25] = t7;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  const t15 = emailForm.handleSubmit(onSubmitEmail);
  let t16;
  if ($[27] !== emailForm.formState.errors.email?.message || $[28] !== t) {
    t16 = t17 => {
      const {
        field
      } = t17;
      return <EmailPhoneInput label={t("Email")} type="email" errorMessage={emailForm.formState.errors.email?.message} {...field} placeholder={t("EmailPlaceholder")} />;
    };
    $[27] = emailForm.formState.errors.email?.message;
    $[28] = t;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== emailForm.control || $[31] !== t16) {
    t17 = <Controller control={emailForm.control} name="email" render={t16} />;
    $[30] = emailForm.control;
    $[31] = t16;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== emailForm.formState.errors.password?.message || $[34] !== t) {
    t18 = t19 => {
      const {
        field: field_0
      } = t19;
      return <AuthPasswordInput label={t("Password")} placeholder={t("PasswordPlaceholder")} errorMessage={emailForm.formState.errors.password?.message} {...field_0} />;
    };
    $[33] = emailForm.formState.errors.password?.message;
    $[34] = t;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== emailForm.control || $[37] !== t18) {
    t19 = <Controller control={emailForm.control} name="password" render={t18} />;
    $[36] = emailForm.control;
    $[37] = t18;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  const t20 = `/${locale}/auth/forgot-password`;
  let t21;
  if ($[39] !== t) {
    t21 = t("ForgotPassword");
    $[39] = t;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  const t22 = `${t21}?`;
  let t23;
  if ($[41] !== t20 || $[42] !== t22) {
    t23 = <Link href={t20} className="self-end font-secondary text-[16px] font-bold uppercase text-white hover:text-white/60">{t22}</Link>;
    $[41] = t20;
    $[42] = t22;
    $[43] = t23;
  } else {
    t23 = $[43];
  }
  let t24;
  if ($[44] !== t) {
    t24 = t("LogIn");
    $[44] = t;
    $[45] = t24;
  } else {
    t24 = $[45];
  }
  let t25;
  if ($[46] !== isLoading || $[47] !== t24) {
    t25 = <Button size="2md" variant="white" className="w-full rounded-[14px]" loading={isLoading} htmlType="submit">{t24}</Button>;
    $[46] = isLoading;
    $[47] = t24;
    $[48] = t25;
  } else {
    t25 = $[48];
  }
  let t26;
  if ($[49] !== t) {
    t26 = t("Terms.Continuing");
    $[49] = t;
    $[50] = t26;
  } else {
    t26 = $[50];
  }
  const t27 = `/${locale}/terms`;
  let t28;
  if ($[51] !== t) {
    t28 = t("Terms.Terms");
    $[51] = t;
    $[52] = t28;
  } else {
    t28 = $[52];
  }
  let t29;
  if ($[53] !== t27 || $[54] !== t28) {
    t29 = <Link className="text-white" href={t27}>{t28}{" "}</Link>;
    $[53] = t27;
    $[54] = t28;
    $[55] = t29;
  } else {
    t29 = $[55];
  }
  let t30;
  if ($[56] !== t) {
    t30 = t("Terms.and");
    $[56] = t;
    $[57] = t30;
  } else {
    t30 = $[57];
  }
  const t31 = `/${locale}/privacy-policy`;
  let t32;
  if ($[58] !== t) {
    t32 = t("Terms.PrivacyPolicy");
    $[58] = t;
    $[59] = t32;
  } else {
    t32 = $[59];
  }
  let t33;
  if ($[60] !== t31 || $[61] !== t32) {
    t33 = <Link className="text-white" href={t31}>{t32}</Link>;
    $[60] = t31;
    $[61] = t32;
    $[62] = t33;
  } else {
    t33 = $[62];
  }
  let t34;
  if ($[63] !== t26 || $[64] !== t29 || $[65] !== t30 || $[66] !== t33) {
    t34 = <span className="text-[14px] text-[#AAAAB6]">{t26}{" "}{t29}{t30}{" "}{t33}.</span>;
    $[63] = t26;
    $[64] = t29;
    $[65] = t30;
    $[66] = t33;
    $[67] = t34;
  } else {
    t34 = $[67];
  }
  let t35;
  if ($[68] !== t25 || $[69] !== t34) {
    t35 = <div className="flex w-full flex-col items-center gap-4 pt-6 tablet:pt-10">{t25}{t34}</div>;
    $[68] = t25;
    $[69] = t34;
    $[70] = t35;
  } else {
    t35 = $[70];
  }
  const t36 = phoneForm.handleSubmit(onSubmitPhone);
  const t37 = `flex w-full flex-col gap-6${loginMode !== "email" ? "" : " hidden"}`;
  let t38;
  if ($[71] !== phoneForm.formState.errors.phone?.message || $[72] !== t) {
    t38 = t39 => {
      const {
        field: field_1
      } = t39;
      return <EmailPhoneInput label={t("Phone")} type="phone" errorMessage={phoneForm.formState.errors.phone?.message} {...field_1} placeholder={t("PhonePlaceholder")} />;
    };
    $[71] = phoneForm.formState.errors.phone?.message;
    $[72] = t;
    $[73] = t38;
  } else {
    t38 = $[73];
  }
  let t39;
  if ($[74] !== phoneForm.control || $[75] !== t38) {
    t39 = <Controller control={phoneForm.control} name="phone" render={t38} />;
    $[74] = phoneForm.control;
    $[75] = t38;
    $[76] = t39;
  } else {
    t39 = $[76];
  }
  let t40;
  if ($[77] !== phoneForm.formState.errors.password?.message || $[78] !== t) {
    t40 = t41 => {
      const {
        field: field_2
      } = t41;
      return <AuthPasswordInput label={t("Password")} placeholder={t("PasswordPlaceholder")} errorMessage={phoneForm.formState.errors.password?.message} {...field_2} />;
    };
    $[77] = phoneForm.formState.errors.password?.message;
    $[78] = t;
    $[79] = t40;
  } else {
    t40 = $[79];
  }
  let t41;
  if ($[80] !== phoneForm.control || $[81] !== t40) {
    t41 = <Controller control={phoneForm.control} name="password" render={t40} />;
    $[80] = phoneForm.control;
    $[81] = t40;
    $[82] = t41;
  } else {
    t41 = $[82];
  }
  const t42 = `/${locale}/auth/forgot-password`;
  let t43;
  if ($[83] !== t) {
    t43 = t("ForgotPassword");
    $[83] = t;
    $[84] = t43;
  } else {
    t43 = $[84];
  }
  const t44 = `${t43}?`;
  let t45;
  if ($[85] !== t42 || $[86] !== t44) {
    t45 = <Link href={t42} className="self-end font-secondary text-[16px] font-bold uppercase text-white hover:text-white/60">{t44}</Link>;
    $[85] = t42;
    $[86] = t44;
    $[87] = t45;
  } else {
    t45 = $[87];
  }
  let t46;
  if ($[88] !== t) {
    t46 = t("LogIn");
    $[88] = t;
    $[89] = t46;
  } else {
    t46 = $[89];
  }
  let t47;
  if ($[90] !== isLoading || $[91] !== t46) {
    t47 = <Button size="2md" variant="white" className="w-full rounded-[14px]" loading={isLoading} htmlType="submit">{t46}</Button>;
    $[90] = isLoading;
    $[91] = t46;
    $[92] = t47;
  } else {
    t47 = $[92];
  }
  let t48;
  if ($[93] !== t) {
    t48 = t("Terms.Continuing");
    $[93] = t;
    $[94] = t48;
  } else {
    t48 = $[94];
  }
  const t49 = `/${locale}/terms`;
  let t50;
  if ($[95] !== t) {
    t50 = t("Terms.Terms");
    $[95] = t;
    $[96] = t50;
  } else {
    t50 = $[96];
  }
  let t51;
  if ($[97] !== t49 || $[98] !== t50) {
    t51 = <Link className="text-white" href={t49}>{t50}{" "}</Link>;
    $[97] = t49;
    $[98] = t50;
    $[99] = t51;
  } else {
    t51 = $[99];
  }
  let t52;
  if ($[100] !== t) {
    t52 = t("Terms.and");
    $[100] = t;
    $[101] = t52;
  } else {
    t52 = $[101];
  }
  const t53 = `/${locale}/privacy-policy`;
  let t54;
  if ($[102] !== t) {
    t54 = t("Terms.PrivacyPolicy");
    $[102] = t;
    $[103] = t54;
  } else {
    t54 = $[103];
  }
  let t55;
  if ($[104] !== t53 || $[105] !== t54) {
    t55 = <Link className="text-white" href={t53}>{t54}</Link>;
    $[104] = t53;
    $[105] = t54;
    $[106] = t55;
  } else {
    t55 = $[106];
  }
  let t56;
  if ($[107] !== t48 || $[108] !== t51 || $[109] !== t52 || $[110] !== t55) {
    t56 = <span className="text-[14px] text-[#AAAAB6]">{t48}{" "}{t51}{t52}{" "}{t55}.</span>;
    $[107] = t48;
    $[108] = t51;
    $[109] = t52;
    $[110] = t55;
    $[111] = t56;
  } else {
    t56 = $[111];
  }
  let t57;
  if ($[112] !== t47 || $[113] !== t56) {
    t57 = <div className="flex w-full flex-col items-center gap-4 pt-6 tablet:pt-10">{t47}{t56}</div>;
    $[112] = t47;
    $[113] = t56;
    $[114] = t57;
  } else {
    t57 = $[114];
  }
  let t58;
  if ($[115] !== t36 || $[116] !== t37 || $[117] !== t39 || $[118] !== t41 || $[119] !== t45 || $[120] !== t57) {
    t58 = <form onSubmit={t36} className={t37}>{t39}{t41}{t45}{t57}</form>;
    $[115] = t36;
    $[116] = t37;
    $[117] = t39;
    $[118] = t41;
    $[119] = t45;
    $[120] = t57;
    $[121] = t58;
  } else {
    t58 = $[121];
  }
  return <div className="flex h-[100%] w-full flex-col items-center justify-center gap-6 p-4  tablet:gap-10 tablet:p-[60px]">{t14}<form onSubmit={t15} className={`flex w-full flex-col gap-6${loginMode === "email" ? "" : " hidden"}`}>{t17}{t19}{t23}{t35}</form>{t58}</div>;
};
function _temp() {
  return amplitude.track("signup_click");
}